import type { LiveActionContent } from '@/components/proposals/shared/useAutoSaveCoordination';
import { logger } from '@/lib/logger';

import type { ProposalDetail, ProposedAction } from '../core/types';

// Drops actions with decrypt errors before building — result.length may be less than actions.length.
/**
 * Matches each valid (non decrypt-error) action to live editor content by
 * `execution_order` first, falling back to `id`. `execution_order` is stable
 * across an autosave version bump (which rewrites action ids), so this is
 * what lets the backend's `_check_expected_actions` guard fire on a genuine
 * agent edit (in-place or new-version) without false-firing on the user's own
 * autosave version bump — see the phase objective for the full rationale.
 *
 * Reorder-race guard: if an agent reorders actions between the frontend's
 * fresh-fetch and the approve POST, the live-content snapshot's
 * `executionOrder` values no longer correspond to the same actions as before.
 * When the execution_order-matched live entry's `actionId` belongs to a
 * DIFFERENT fresh action (i.e. its id appears in fresh actions but is not this
 * action's id), the exec-order match is unreliable and we fall back to the
 * id-based lookup instead.
 */
export function buildExpectedActionPayloads(
  actions: ProposedAction[],
  liveContent: LiveActionContent[]
): Array<{ id: number; payload: Record<string, unknown> }> {
  const validActions = actions.filter(
    a => !(a.payload && typeof a.payload === 'object' && 'error' in a.payload)
  );

  // Map execution_order → the full live entry (not just payload) so we can
  // inspect actionId for the reorder-race check below.
  const byExecOrder = new Map<number, LiveActionContent>(
    liveContent
      .filter(c => c.executionOrder != null)
      .map(c => [c.executionOrder as number, c])
  );
  const byId = new Map<number, Record<string, unknown>>(
    liveContent.map(c => [c.actionId, c.payload])
  );
  const freshActionIds = new Set(validActions.map(a => a.id));

  return validActions.map(a => {
    const execOrderEntry = byExecOrder.get(a.execution_order);
    if (execOrderEntry != null) {
      // If the live entry's actionId is a DIFFERENT fresh action's id, an
      // agent reorder swapped execution_orders between capture and approve.
      // Using this entry would attach the wrong action's content to this id.
      if (
        execOrderEntry.actionId !== a.id &&
        freshActionIds.has(execOrderEntry.actionId)
      ) {
        logger.warn(
          '[buildExpectedActionPayloads] execution_order collision: agent reorder detected between fetch and approve — falling back to id-matched content',
          {
            actionId: a.id,
            executionOrder: a.execution_order,
            collisionLiveActionId: execOrderEntry.actionId,
          }
        );
        return {
          id: a.id,
          payload:
            byId.get(a.id) ?? (a.payload as unknown as Record<string, unknown>),
        };
      }
      return { id: a.id, payload: execOrderEntry.payload };
    }
    return {
      id: a.id,
      payload:
        byId.get(a.id) ?? (a.payload as unknown as Record<string, unknown>),
    };
  });
}

export function buildApproveArgs(
  source: ProposalDetail,
  localSelectedIds: number[] | undefined,
  liveContent: LiveActionContent[]
): {
  effectiveSelectedIds: number[] | undefined;
  actionPayloads: Array<{ id: number; payload: Record<string, unknown> }>;
  decryptErrorCount: number;
} {
  const actionPayloads = buildExpectedActionPayloads(
    source.actions,
    liveContent
  );
  const decryptErrorCount = source.actions.length - actionPayloads.length;

  // Resolve the selected-action set. Two independent staleness sources can
  // diverge here:
  //   1. Autosave version changes rewrite action IDs, so `localSelectedIds`
  //      (captured in the UI) can reference IDs that no longer exist in the
  //      freshly-fetched `source`.
  //   2. Selection sync is debounced (~300ms), so `source`'s `is_selected`
  //      state can lag the user's most recent toggle.
  // Prefer the user's captured selection when every selected ID still exists
  // in the fresh data — no version change touched the selected actions, so
  // honoring the capture respects a toggle that raced the debounce. If any
  // selected ID is gone (a version change rewrote IDs), fall back to the
  // server-persisted `is_selected` state, which survives version-ID changes.
  let effectiveSelectedIds: number[] | undefined;
  if (localSelectedIds === undefined) {
    effectiveSelectedIds = undefined;
  } else {
    const freshActionIds = new Set(source.actions.map(a => a.id));
    const localIdsAreCurrent = localSelectedIds.every(id =>
      freshActionIds.has(id)
    );
    effectiveSelectedIds = localIdsAreCurrent
      ? localSelectedIds.filter(id => freshActionIds.has(id))
      : source.actions.filter(a => a.is_selected !== false).map(a => a.id);
  }

  return { effectiveSelectedIds, actionPayloads, decryptErrorCount };
}
